import { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/Button"
import { CenteringContainer } from "@/components/CenteringContainer"
import { OverlayDiv } from "@/components/OverlayDiv"

import { useIsNotionalValid } from "./NotionalInput"
import { TileStates, useTileState } from "./tile.state"
import { useTileCurrencyPair } from "./TileContext"
import {
  onCancelRfq,
  onQuoteRequest,
  QuoteStateStage,
  useIsRfq,
  useRfqState,
} from "./TileFooter/RfqTimer/rfq.state"

const buttonState = (quoteState: QuoteStateStage) => {
  switch (quoteState) {
    case QuoteStateStage.Init:
      return {
        buttonText: "liveRates.rfq.initiate",
        buttonClickHandler: onQuoteRequest,
        cancellable: false,
      }
    case QuoteStateStage.Requested:
      return {
        buttonText: "liveRates.rfq.cancel",
        buttonClickHandler: onCancelRfq,
        cancellable: true,
      }
    default:
      return {
        buttonText: "liveRates.rfq.requote",
        buttonClickHandler: onQuoteRequest,
        cancellable: false,
      }
  }
}

interface TileRfqButtonProps {
  cancellable: boolean
  disabled: boolean
  onClick: () => void
}

export const TileRfqButtonInner = ({
  cancellable,
  ...props
}: PropsWithChildren<TileRfqButtonProps>) => {
  return (
    <OverlayDiv>
      <CenteringContainer>
        <Button
          className={
            cancellable ? "bg-error-primary hover:bg-error-secondary" : ""
          }
          variant="brand"
          size="sm"
          data-testid="rfqButton"
          {...props}
        />
      </CenteringContainer>
    </OverlayDiv>
  )
}

export const TileRfqButton = () => {
  const { t } = useTranslation()
  const isRfq = useIsRfq()
  const { stage } = useRfqState()
  const { symbol } = useTileCurrencyPair()
  const { buttonText, buttonClickHandler, cancellable } = buttonState(stage)
  const isExecuting = useTileState(symbol).status === TileStates.Started
  const validNotional = useIsNotionalValid()

  return isRfq && stage !== QuoteStateStage.Received && !isExecuting ? (
    <TileRfqButtonInner
      cancellable={cancellable}
      disabled={!validNotional}
      onClick={() => {
        if (validNotional) {
          buttonClickHandler(symbol)
        }
      }}
    >
      {t(buttonText)}
    </TileRfqButtonInner>
  ) : null
}
